import React, { useState, useRef, forwardRef, useCallback, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import '../../../styles/CampingCelebration.css';
import bookOpeningSound from '../../../assets/sounds/book-opening.mp3';
import logicaLogo from '../../../assets/images/Logica (Black and White).webp';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/* ─── Flipbook page wrapper ─────────────────────────────────────── */
const Page = forwardRef(({ children, className = '' }, ref) => (
  <div className={`cc-page ${className}`} ref={ref}>
    {children}
  </div>
));
Page.displayName = 'Page';

/* ─── Main Component ────────────────────────────────────────────── */
const CampingCelebration = () => {
  const [phase, setPhase]             = useState('cover');
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping]   = useState(false);
  const [sparklePos, setSparklePos]   = useState({ top: 0, left: 0 });
  const [bookReady, setBookReady]     = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth <= 768);
  const [showTapHint, setShowTapHint] = useState(false);
  const [countdown, setCountdown]     = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [showRsvp, setShowRsvp]       = useState(false);
  const [rsvpDone, setRsvpDone]       = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError]     = useState('');
  const [rsvpData, setRsvpData]       = useState({ name: '', attending: '', guests: '1', message: '' });

  const bookRef    = useRef(null);
  const audioRef   = useRef(null);
  const wrapperRef = useRef(null);

  /* ── Event data ─────────────────────────────────────────────── */
  const ev = {
    childName:  'Mateo',
    childFull:  'Mateo Andres Villanueva',
    parents:    { father: 'Mr. Arnold Villanueva', mother: 'Mrs. Caralyn Villanueva' },
    eventDate:  'May 9, 2026',
    eventDay:   'Saturday',
    timeChrist: '10:00 AM',
    timeBday:   '2:00 PM',
    church:     'Our Lady of Guadalupe Parish',
    churchAddr: 'Guadalupe Nuevo, Makati City',
    venue:      'The Campsite Events Place',
    venueAddr:  'Antipolo City, Rizal',
    verse:      '"Before I formed you in the womb I knew you, before you were born I set you apart."',
    verseRef:   'Jeremiah 1:5',
    hashtag:    '#MateoTurnsONE',
    rsvpBy:     'May 1, 2026',
    godparents: [
      { ninong: 'Jose Dela Cruz',  ninang: 'Ana Dela Cruz'  },
      { ninong: 'Carlos Reyes',    ninang: 'Lisa Reyes'     },
      { ninong: 'Edward Santos',   ninang: 'Grace Santos'   },
      { ninong: 'Roland Torres',   ninang: 'Marina Torres'  },
      { ninong: 'Dominic Lim',     ninang: 'Patricia Lim'   },
    ],
    program: [
      { time: '10:00 AM', event: 'Arrival of Guests'           },
      { time: '10:30 AM', event: 'Christening Mass'             },
      { time: '12:00 NN', event: 'Travel to Party Venue'        },
      { time: '2:00 PM',  event: 'Birthday Celebration Begins'  },
      { time: '2:30 PM',  event: 'Welcome & Program Proper'     },
      { time: '3:00 PM',  event: 'Food & Festivities'           },
      { time: '4:00 PM',  event: 'Blowing of Candles 🎂'       },
      { time: '4:30 PM',  event: 'Games & Photo Booth'          },
      { time: '6:00 PM',  event: 'End of Celebration'           },
    ],
  };

  const TOTAL = 8;

  /* ── Countdown ──────────────────────────────────────────────── */
  useEffect(() => {
    const target = new Date('May 9, 2026 00:00:00').getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff > 0) setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Resize ─────────────────────────────────────────────────── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  /* ── Audio ──────────────────────────────────────────────────── */
  useEffect(() => {
    const a = new Audio(bookOpeningSound);
    a.volume = 0.45; a.preload = 'auto'; a.load();
    a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
    audioRef.current = a;
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  /* ── Tap hint ───────────────────────────────────────────────── */
  useEffect(() => {
    if (phase !== 'cover') return;
    const t = setTimeout(() => setShowTapHint(true), 2500);
    return () => clearTimeout(t);
  }, [phase]);

  /* ── Book ready ─────────────────────────────────────────────── */
  useEffect(() => {
    if (phase === 'open') {
      const t = setTimeout(() => { window.dispatchEvent(new Event('resize')); setBookReady(true); }, 120);
      return () => clearTimeout(t);
    }
  }, [phase]);

  /* ── Handlers ───────────────────────────────────────────────── */
  const playPage = useCallback(() => {
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
  }, []);

  const openBook  = () => { if (phase === 'cover') { setShowTapHint(false); setPhase('open'); } };
  const closeBook = () => {
    if (bookRef.current) bookRef.current.pageFlip().flip(0);
    setTimeout(() => { setCurrentPage(0); setPhase('cover'); }, 350);
  };

  const calcSparkle = useCallback(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current.querySelector('.stf__parent');
    if (!el) return;
    const wr = wrapperRef.current.getBoundingClientRect();
    const br = el.getBoundingClientRect();
    setSparklePos({ top: br.top - wr.top - 20, left: br.right - wr.left - 55 });
  }, []);

  const onFlip = useCallback((e) => {
    calcSparkle();
    setIsFlipping(true); setCurrentPage(e.data); playPage();
    setTimeout(() => setIsFlipping(false), 700);
  }, [playPage, calcSparkle]);

  const flipNext = () => bookRef.current?.pageFlip().flipNext();
  const flipPrev = () => bookRef.current?.pageFlip().flipPrev();

  /* ── RSVP ───────────────────────────────────────────────────── */
  const handleRsvpChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(p => ({ ...p, [name]: value }));
    if (rsvpError) setRsvpError('');
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault(); setRsvpError(''); setRsvpLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rsvpData, invitationId: 'camping-mateo', childName: ev.childName }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.message || 'Failed to submit RSVP.');
      setRsvpDone(true);
    } catch (err) { setRsvpError(err.message); }
    finally { setRsvpLoading(false); }
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div className="cc-root">

      {/* ── Sky & atmosphere ── */}
      <div className="cc-sky" aria-hidden="true">
        {[...Array(45)].map((_, i) => (
          <div key={i} className="cc-star"
            style={{
              left: `${(i * 7.3 + 2) % 100}%`,
              top:  `${(i * 11.7 + 1) % 65}%`,
              width: `${1 + (i % 3)}px`, height: `${1 + (i % 3)}px`,
              animationDelay: `${(i * 0.37) % 4}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        ))}
        {/* Moon */}
        <div className="cc-moon" />
      </div>

      {/* ── Fireflies ── */}
      <div className="cc-fireflies" aria-hidden="true">
        {[...Array(18)].map((_, i) => (
          <div key={i} className="cc-ff"
            style={{
              left: `${(i * 5.6 + 1) % 95}%`,
              bottom: `${8 + (i * 17) % 40}%`,
              animationDuration: `${3.5 + (i % 4)}s`,
              animationDelay: `${(i * 0.45) % 5}s`,
            }}
          />
        ))}
      </div>

      {/* ── Forest silhouette ── */}
      <div className="cc-forest" aria-hidden="true">
        {[...Array(14)].map((_, i) => (
          <div key={i} className="cc-forest-tree"
            style={{
              left: `${(i * 7.2 - 1) % 100}%`,
              height: `${70 + (i * 23) % 90}px`,
              width:  `${38 + (i * 13) % 32}px`,
              opacity: 0.6 + (i % 3) * 0.1,
            }}
          />
        ))}
      </div>

      {/* ════════════════════════════════════════════
          COVER SCREEN
      ════════════════════════════════════════════ */}
      {phase === 'cover' && (
        <div className="cc-cover-wrap" onClick={openBook} role="button" aria-label="Open invitation">

          {/* Bunting flags */}
          <div className="cc-bunting" aria-hidden="true">
            <div className="cc-bunting-rope" />
            {['#e07b39','#f5c842','#2d5a27','#e07b39','#f5c842','#2d5a27','#e07b39','#f5c842','#2d5a27'].map((c, i) => (
              <div key={i} className="cc-flag" style={{ left: `${i * 11 + 3}%`, background: c, animationDelay: `${i * 0.12}s` }} />
            ))}
          </div>

          <div className="cc-cover-card">
            {/* Card header stripe */}
            <div className="cc-card-stripe" />

            {/* ONE — illustrated log */}
            <div className="cc-ohc-section">
              <div className="cc-ohc-one-row">
                <div className="cc-log-o">
                  <div className="cc-lr cc-lr1" /><div className="cc-lr cc-lr2" /><div className="cc-lr cc-lr3" />
                  <div className="cc-lgrain" /><div className="cc-lgrain cc-lg2" />
                </div>
                <span className="cc-one-ne">NE</span>
              </div>
              <h1 className="cc-happy-camper">HAPPY CAMPER</h1>
            </div>

            {/* Twig divider */}
            <div className="cc-twig-div">
              <div className="cc-twig-line" /><span className="cc-twig-leaf">🌿</span><div className="cc-twig-line" />
            </div>

            <p className="cc-cover-tagline">HIKE ON OVER TO HELP US CELEBRATE</p>

            {/* Name */}
            <div className="cc-cover-name-block">
              <h2 className="cc-cover-name">{ev.childName}<span className="cc-cover-apostrophe">'s</span></h2>
              <p className="cc-cover-milestone">First Birthday &amp; Christening</p>
            </div>

            {/* Date */}
            <div className="cc-cover-date-pill">
              <span className="cc-cdp-month">MAY</span>
              <span className="cc-cdp-day">9</span>
              <span className="cc-cdp-year">2026</span>
            </div>

            {/* Illustrated scene */}
            <div className="cc-cover-scene" aria-hidden="true">
              {/* Trees */}
              <div className="cc-cs-tree cs-tl" /><div className="cc-cs-trunk cs-tl" />
              <div className="cc-cs-tree cs-tr" /><div className="cc-cs-trunk cs-tr" />
              <div className="cc-cs-tree cs-tll" /><div className="cc-cs-trunk cs-tll" />
              <div className="cc-cs-tree cs-trr" /><div className="cc-cs-trunk cs-trr" />
              {/* Ground */}
              <div className="cc-ground" />
              {/* Tent */}
              <div className="cc-tent">
                <div className="cc-tent-body" /><div className="cc-tent-door" />
                <div className="cc-tent-flag-pole"><div className="cc-tent-flag-tri" /></div>
              </div>
              {/* Campfire */}
              <div className="cc-fire">
                <div className="cc-fire-stones" />
                <div className="cc-fire-log-l" /><div className="cc-fire-log-r" />
                <div className="cc-flame-wrap">
                  <div className="cc-fl cc-fl1" /><div className="cc-fl cc-fl2" /><div className="cc-fl cc-fl3" />
                </div>
              </div>
              {/* Bear */}
              <div className="cc-scene-bear">🐻</div>
              {/* Mushroom */}
              <div className="cc-mushroom">🍄</div>
            </div>
          </div>

          {/* Tap hint */}
          {showTapHint && (
            <div className="cc-tap-hint" aria-hidden="true">
              <div className="cc-tap-pulse" />
              <div className="cc-tap-pulse cc-tp2" />
              <div className="cc-tap-hand">👆</div>
              <p className="cc-tap-label">Tap to open</p>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════
          FLIPBOOK OPEN STATE
      ════════════════════════════════════════════ */}
      {phase === 'open' && (
        <div className="cc-book-outer">

          {/* Header bar */}
          <div className="cc-book-header">
            <button className="cc-close-btn" onClick={closeBook}>✕ Close</button>
            <div className="cc-book-title">🏕️ {ev.childName}'s Big Day</div>
            <div className="cc-page-count">{currentPage + 1} / {TOTAL}</div>
          </div>

          {/* Nav arrows */}
          <button className="cc-nav-arrow cc-nav-prev" onClick={flipPrev} disabled={currentPage === 0} aria-label="Previous page">‹</button>
          <button className="cc-nav-arrow cc-nav-next" onClick={flipNext} disabled={currentPage >= TOTAL - 1} aria-label="Next page">›</button>

          <div className={`cc-book-stage ${bookReady ? 'ready' : ''}`}>
            <div ref={wrapperRef} className="cc-book-wrap">

              {/* Sparkle effect */}
              <div
                className={`cc-flip-sparkle ${isFlipping ? 'on' : ''}`}
                style={{ top: sparklePos.top, left: sparklePos.left }}
                aria-hidden="true"
              >
                {['✦','✧','⋆','✦','✧','⋆','✦','✧'].map((s, i) => (
                  <span key={i} className={`cc-sp cc-sp${i}`}>{s}</span>
                ))}
              </div>

              <HTMLFlipBook
                ref={bookRef}
                width={isMobile ? 340 : 410}
                height={isMobile ? 530 : 580}
                size="stretch"
                minWidth={isMobile ? 300 : 350}
                maxWidth={isMobile ? 390 : 470}
                minHeight={isMobile ? 470 : 500}
                maxHeight={isMobile ? 600 : 660}
                drawShadow
                flippingTime={680}
                usePortrait={isMobile}
                startZIndex={0}
                autoSize={false}
                maxShadowOpacity={0.4}
                showCover
                mobileScrollSupport={false}
                swipeDistance={22}
                clickEventForward
                useMouseEvents
                className="cc-flipbook"
                onFlip={onFlip}
                style={{ margin: '0 auto' }}
              >

                {/* ══ P1: Bible Verse / Welcome ══ */}
                <Page className="pg-forest">
                  <div className="pg-in">
                    <div className="pg-forest-deco" aria-hidden="true">
                      <div className="pg-fd-tree pf-l" /><div className="pg-fd-tree pf-r" />
                    </div>
                    <div className="pg-verse-wrap">
                      <div className="pg-cross-icon">✝</div>
                      <blockquote className="pg-verse">{ev.verse}</blockquote>
                      <p className="pg-verse-ref">— {ev.verseRef}</p>
                    </div>
                    <div className="pg-welcome-block">
                      <p className="pg-wl-pre">Welcome to</p>
                      <p className="pg-wl-name">{ev.childName}'s</p>
                      <p className="pg-wl-sub">Little Adventure!</p>
                    </div>
                    <div className="pg-campfire-mini" aria-hidden="true">
                      <div className="pg-cf-log" />
                      <div className="pg-cf-fl cf1" /><div className="pg-cf-fl cf2" /><div className="pg-cf-fl cf3" />
                    </div>
                  </div>
                </Page>

                {/* ══ P2: Invitation ══ */}
                <Page className="pg-parchment">
                  <div className="pg-in">
                    <div className="pg-parch-header">
                      <div className="pg-parch-leaf">🍃 🌿 🍃</div>
                    </div>
                    <p className="pg-with-families">TOGETHER WITH OUR FAMILIES</p>
                    <p className="pg-with-families">AND THE BLESSINGS OF GOD ABOVE</p>
                    <div className="pg-parents-block">
                      <p className="pg-parent-name">{ev.parents.father}</p>
                      <div className="pg-parent-amp">&amp;</div>
                      <p className="pg-parent-name">{ev.parents.mother}</p>
                    </div>
                    <p className="pg-invite-line">JOYFULLY INVITE YOU TO WITNESS</p>
                    <p className="pg-invite-line">THE CELEBRATION OF THEIR SON</p>
                    <div className="pg-child-hero">
                      <h2 className="pg-child-name">{ev.childName}</h2>
                      <p className="pg-child-fullname">{ev.childFull}</p>
                    </div>
                    <div className="pg-event-ribbon">
                      <p className="pg-er-title">FIRST BIRTHDAY &amp; CHRISTENING</p>
                      <div className="pg-er-date">
                        <span className="pg-erd-m">MAY</span>
                        <span className="pg-erd-d">9</span>
                        <span className="pg-erd-y">2026</span>
                      </div>
                    </div>
                    <div className="pg-foot-deco">🌲&nbsp;🏕️&nbsp;🌲</div>
                  </div>
                </Page>

                {/* ══ P3: Christening ══ */}
                <Page className="pg-parchment">
                  <div className="pg-in">
                    <div className="pg-section-header sh-green">
                      <span className="pg-sh-icon">✝️</span>
                      <h3 className="pg-sh-title">Christening</h3>
                    </div>

                    <div className="pg-ev-card">
                      <div className="pg-evc-time">
                        <span className="pg-evc-tl">TIME</span>
                        <span className="pg-evc-tv">{ev.timeChrist}</span>
                      </div>
                      <div className="pg-evc-venue">
                        <p className="pg-evc-vn">{ev.church}</p>
                        <p className="pg-evc-va">{ev.churchAddr}</p>
                        <a
                          className="pg-map-btn"
                          href="https://maps.google.com/?q=Our+Lady+of+Guadalupe+Parish+Makati"
                          target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >📍 Open Maps</a>
                      </div>
                    </div>

                    <div className="pg-divider">🌿 Ninong &amp; Ninang 🌿</div>

                    <div className="pg-gp-table">
                      <div className="pg-gp-head">
                        <span>Ninong</span><span>Ninang</span>
                      </div>
                      {ev.godparents.map((gp, i) => (
                        <div key={i} className="pg-gp-row">
                          <span>{gp.ninong}</span>
                          <span className="pg-gp-dot">·</span>
                          <span>{gp.ninang}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pg-foot-deco">✝️&nbsp;🕊️&nbsp;✝️</div>
                  </div>
                </Page>

                {/* ══ P4: Birthday Party ══ */}
                <Page className="pg-parchment">
                  <div className="pg-in">
                    <div className="pg-section-header sh-orange">
                      <span className="pg-sh-icon">🎂</span>
                      <h3 className="pg-sh-title">Birthday Party</h3>
                    </div>

                    <div className="pg-ev-card">
                      <div className="pg-evc-time">
                        <span className="pg-evc-tl">TIME</span>
                        <span className="pg-evc-tv">{ev.timeBday}</span>
                      </div>
                      <div className="pg-evc-venue">
                        <p className="pg-evc-vn">{ev.venue}</p>
                        <p className="pg-evc-va">{ev.venueAddr}</p>
                        <a
                          className="pg-map-btn"
                          href="https://maps.google.com/?q=Campsite+Events+Place+Antipolo"
                          target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >📍 Open Maps</a>
                      </div>
                    </div>

                    <div className="pg-divider">🏕️ Program 🏕️</div>

                    <div className="pg-program">
                      {ev.program.map((item, i) => (
                        <div key={i} className="pg-prog-row">
                          <span className="pg-pr-time">{item.time}</span>
                          <div className="pg-pr-dot" />
                          <span className="pg-pr-ev">{item.event}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Page>

                {/* ══ P5: Countdown & Save the Date ══ */}
                <Page className="pg-parchment">
                  <div className="pg-in">
                    <div className="pg-section-header sh-forest">
                      <span className="pg-sh-icon">⏳</span>
                      <h3 className="pg-sh-title">Counting Down</h3>
                    </div>

                    <div className="pg-countdown">
                      {[{ v: countdown.d, l: 'DAYS' }, { v: countdown.h, l: 'HRS' }, { v: countdown.m, l: 'MIN' }, { v: countdown.s, l: 'SEC' }].map((c, i) => (
                        <React.Fragment key={i}>
                          <div className="pg-cd-unit">
                            <span className="pg-cd-num">{String(c.v).padStart(2, '0')}</span>
                            <span className="pg-cd-lbl">{c.l}</span>
                          </div>
                          {i < 3 && <span className="pg-cd-colon">:</span>}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="pg-divider">🏕️ Save the Date 🏕️</div>

                    <div className="pg-save-card">
                      <p className="pg-sc-title">MATEO'S BIG DAY</p>
                      <p className="pg-sc-date">{ev.eventDate}</p>
                      <p className="pg-sc-day">{ev.eventDay}</p>
                      <div className="pg-sc-events">
                        <div className="pg-sc-ev">
                          <span className="pg-sc-ev-icon">✝️</span>
                          <span>{ev.timeChrist} — Christening at</span>
                          <span className="pg-sc-ev-place">{ev.church}</span>
                        </div>
                        <div className="pg-sc-ev-sep">🌿</div>
                        <div className="pg-sc-ev">
                          <span className="pg-sc-ev-icon">🎂</span>
                          <span>{ev.timeBday} — Birthday Party at</span>
                          <span className="pg-sc-ev-place">{ev.venue}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* ══ P6: Snap, Gifts & RSVP ══ */}
                <Page className="pg-parchment">
                  <div className="pg-in">
                    <div className="pg-section-header sh-brown">
                      <span className="pg-sh-icon">📸</span>
                      <h3 className="pg-sh-title">Snap &amp; Share</h3>
                    </div>
                    <p className="pg-snap-text">CAPTURE AND SHARE YOUR MEMORIES</p>
                    <div className="pg-hashtag-wrap">
                      <p className="pg-hashtag">{ev.hashtag}</p>
                    </div>

                    <div className="pg-divider">🎁 Gift Note 🎁</div>

                    <div className="pg-gift-box">
                      <p>Your presence is the greatest gift!</p>
                      <p>Should you wish to bless little <strong>{ev.childName}</strong>,</p>
                      <p>any token of love is warmly appreciated. 🧸</p>
                    </div>

                    <div className="pg-divider">💌 RSVP 💌</div>

                    <p className="pg-rsvp-note">Kindly respond before {ev.rsvpBy}</p>
                    <button
                      className="pg-rsvp-btn"
                      onClick={e => { e.stopPropagation(); setShowRsvp(true); }}
                    >🏕️ RSVP Now</button>
                  </div>
                </Page>

                {/* ══ P7: Thank You ══ */}
                <Page className="pg-dark">
                  <div className="pg-in pg-ty">
                    {/* Decorative campfire art */}
                    <div className="pg-ty-fire" aria-hidden="true">
                      <div className="pg-ty-fl ty-f1" /><div className="pg-ty-fl ty-f2" /><div className="pg-ty-fl ty-f3" />
                      <div className="pg-ty-log-l" /><div className="pg-ty-log-r" />
                    </div>
                    <h2 className="pg-ty-title">Thank You</h2>
                    <p className="pg-ty-msg">for helping us celebrate</p>
                    <p className="pg-ty-msg">our little one!</p>
                    <div className="pg-ty-name">{ev.childName}</div>
                    <p className="pg-ty-date">{ev.eventDate}</p>
                    <div className="pg-ty-icons">🐻 🏕️ 🎂 ✝️ 🎉</div>
                    <p className="pg-ty-parents">{ev.parents.father} &amp; {ev.parents.mother}</p>
                  </div>
                </Page>

                {/* ══ P8: Back cover (even-count pairing) ══ */}
                <Page className="pg-back">
                  <div className="pg-in pg-back-in">
                    <div className="pg-back-trees" aria-hidden="true">
                      <div className="pg-bt pg-bt1" /><div className="pg-bt pg-bt2" /><div className="pg-bt pg-bt3" />
                    </div>
                    <div className="pg-back-content">
                      <blockquote className="pg-back-verse">{ev.verse}</blockquote>
                      <p className="pg-back-ref">{ev.verseRef}</p>
                      <p className="pg-back-fire">🔥</p>
                    </div>
                  </div>
                </Page>

              </HTMLFlipBook>
            </div>
          </div>

          {isMobile && <p className="cc-swipe-hint">← Swipe to turn pages →</p>}
        </div>
      )}

      {/* ════════════════════════════════════════════
          RSVP MODAL
      ════════════════════════════════════════════ */}
      {showRsvp && (
        <div className="cc-rsvp-overlay" onClick={() => setShowRsvp(false)}>
          <div className="cc-rsvp-modal" onClick={e => e.stopPropagation()}>
            <button className="cc-rsvp-x" onClick={() => setShowRsvp(false)}>✕</button>

            {!rsvpDone ? (
              <>
                <div className="cc-rsvp-head">
                  <div className="cc-rsvp-icon">🏕️</div>
                  <h2>RSVP</h2>
                  <p>Join us at Camp {ev.childName}!</p>
                </div>
                <form onSubmit={handleRsvpSubmit} noValidate>
                  <div className="cc-field">
                    <label htmlFor="rsvp-name">Your Name</label>
                    <input id="rsvp-name" type="text" name="name" value={rsvpData.name} onChange={handleRsvpChange} placeholder="Enter your full name" required />
                  </div>
                  <div className="cc-field">
                    <label>Will you attend?</label>
                    <div className="cc-choices">
                      <label className={`cc-choice ${rsvpData.attending === 'yes' ? 'sel' : ''}`}>
                        <input type="radio" name="attending" value="yes" checked={rsvpData.attending === 'yes'} onChange={handleRsvpChange} required /> 🎉 Joyfully Accept
                      </label>
                      <label className={`cc-choice ${rsvpData.attending === 'no' ? 'sel' : ''}`}>
                        <input type="radio" name="attending" value="no" checked={rsvpData.attending === 'no'} onChange={handleRsvpChange} /> 😢 Regretfully Decline
                      </label>
                    </div>
                  </div>
                  {rsvpData.attending === 'yes' && (
                    <div className="cc-field">
                      <label htmlFor="rsvp-guests">Number of Guests</label>
                      <select id="rsvp-guests" name="guests" value={rsvpData.guests} onChange={handleRsvpChange}>
                        {['1','2','3','4','5+'].map(n => <option key={n} value={n}>{n} Guest{n !== '1' ? 's' : ''}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="cc-field">
                    <label htmlFor="rsvp-msg">Message (Optional)</label>
                    <textarea id="rsvp-msg" name="message" value={rsvpData.message} onChange={handleRsvpChange} placeholder={`Write your wishes for little ${ev.childName}…`} rows="3" />
                  </div>
                  {rsvpError && <p className="cc-rsvp-err">{rsvpError}</p>}
                  <button type="submit" className="cc-rsvp-submit" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Sending…' : '🏕️ Send RSVP'}
                  </button>
                </form>
              </>
            ) : (
              <div className="cc-rsvp-success">
                <div className="cc-rs-icon">🎉</div>
                <h2>Thank You!</h2>
                <p>RSVP received,</p>
                <p className="cc-rs-name">{rsvpData.name}!</p>
                {rsvpData.attending === 'yes'
                  ? <p>We can't wait to see you at Camp {ev.childName}! 🏕️</p>
                  : <p>We'll miss you! Thank you for letting us know. 💕</p>
                }
                <button className="cc-rs-close" onClick={() => setShowRsvp(false)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Watermark ── */}
      <div className="cc-watermark" aria-hidden="true">
        <p>Created with ❤️ by</p>
        <img src={logicaLogo} alt="Logica Technology" />
      </div>

    </div>
  );
};

export default CampingCelebration;
